import React, { useState } from "react";
import { Alert, Button, Form, Space, Typography, message } from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { createTransactionPlan } from "../../api/transactionPlanApi";
import { useTranslation } from "../../i18n/LanguageProvider";
import TransactionPlanFormFields from "./TransactionPlanFormFields";
import { buildTransactionPlanPayload } from "./transactionPlanUi";

const { Title, Text } = Typography;

/**
 * Manual entry of one Гүйлгээний төлөвлөгөө row. The fields mirror the Excel structure so both
 * import paths write the same table.
 */
function ManualTransactionPlanForm({ onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async () => {
        setError("");

        try {
            const values = await form.validateFields();

            setSaving(true);

            const saved = await createTransactionPlan(buildTransactionPlanPayload(values));

            message.success(t("manual.saved"));
            form.resetFields();
            onSaved?.(saved);
        } catch (err) {
            if (err?.errorFields) return;

            const text = err?.message || t("manual.saveFailed");
            setError(text);
            message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
                <Title level={4} className="!mb-1">
                    <Space>
                        <PlusOutlined />
                        {t("manual.title")}
                    </Space>
                </Title>
                <Text type="secondary">{t("manual.hint")}</Text>
            </div>

            {error && (
                <Alert type="error" showIcon message={t("manual.saveError")} description={error} />
            )}

            <Form form={form} layout="vertical" requiredMark autoComplete="off">
                <TransactionPlanFormFields t={t} />

                <Space>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSubmit}
                    >
                        {t("action.save")}
                    </Button>
                    <Button
                        onClick={() => {
                            form.resetFields();
                            setError("");
                        }}
                    >
                        {t("action.clear")}
                    </Button>
                </Space>
            </Form>
        </Space>
    );
}

export default ManualTransactionPlanForm;
